import { LitElement, html, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { MgLayers } from "../../../src/maplayers/mg-layers";
import "../../voyage-popup/src/voyage-popup";
import "../../voyage-tooltip/src/voyage-tooltip-proxy";
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';

@customElement('voy-chart-depths-control')
export class VoyChartDepthsControl extends LitElement
{
    @property({type: Boolean}) shown: boolean = false;

    constructor()
    {
        super();

        this._readDepths();
    }

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            .container {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-right: 15px;
                margin-left: 15px;
                margin-top: 30px;
                margin-bottom: 30px;
                font-family: MaerskText;
            }

            .warning {
                box-sizing: border-box;
                width: 768px;
                max-width: calc(100vw - 40px);
                padding: 20px;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                border: 1px solid var(--warning-yellow-8, yellow);
                border-radius: 3px;
                background-color: rgba(var(--warning-yellow-10-rgb, yellow), 0.63);
                margin-bottom: 30px;
            }

            .depthGraph {
                display: grid;
                grid-template-columns: 150px 16px 156px 16px 150px;
                grid-auto-flow: dense;
            }

            .leftLabel {
                margin-right: 20px;
                align-self: center;
                justify-self: end;
                text-align: end;
                font-size: 14px;
                font-weight: lighter;
                color: var(--neutrals-grey-02, black);
            }

            .depthArea {
                height: 70px;
                border: 1px solid var(--neutrals-grey-04, grey);
                position: relative;
            }

            .shore {
                border-bottom: none;
                background-color: var(--chart-shore-color);
            }

            .nonNavigable {
                box-sizing: border-box;
                font-size: 12px;
                grid-row: span 2;
                writing-mode: vertical-lr;
                transform: rotate(180deg);
                background-color: rgba(var(--voyage-error-red-rgb), 0.1);
                color: var(--voyage-error-red, red);
                border: 1px solid var(--voyage-error-red, red);
                border-radius: 2.5px;
                text-align: center;
            }

            .intertidal {
                background-color: var(--chart-intertidal-color);
                border-bottom: none;
                border-top: none;
            }

            .veryShallow {
                background-color: var(--chart-very-shallow-color);
                border-bottom: none;
                border-top: none;
            }

            .mediumShallow {
                background-color: var(--chart-medium-shallow-color);
                border-bottom: none;
                border-top: none;
            }

            .mediumWater {
                background-color: var(--chart-medium-water-color);
                border-bottom: none;
                border-top: none;
            }

            .deepWater {
                background-color: var(--chart-deep-water-color);
                border-top: none;
            }

            .navigable {
                box-sizing: border-box;
                font-size: 12px;
                grid-row: span 3;
                writing-mode: vertical-lr;
                transform: rotate(180deg);
                background-color: rgba(var(--voyage-good-green-rgb), 0.1);
                color: var(--voyage-good-green, green);
                border: 1px solid var(--voyage-good-green, green);
                border-radius: 2.5px;
                text-align: center;
            }

            .depthInput {
                box-sizing: border-box;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding-left: 15px;
                padding-right: 15px;
                height: 52px;
                background-color: var(--neutrals-grey-10, white);
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                color: var(--neutrals-grey-05, grey);
            }

            .insideInput {
                width: 52px;
                padding: 0;
                position: absolute;
                top: -26px;
                left: 52px;
            }

            .insideInput::after {
                content: "";
                display: block;
                position: absolute;
                height: 1px;
                width: 68px;
                top: 25px;
                left: 51px;
                background-color: var(--neutrals-grey-04, grey);
            }

            input {
                display: inline-block;
                text-align: right;
                width: 35px;
                border: none;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
            }

            .rightLabelContainer {
                position: relative;
                margin-left: 5px;
            }

            .rightLabel {
                display: flex;
                align-items: center;
                position: absolute;
                top: -1px;
                transform: translateY(-50%);
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
            }

            .questionMark {
                margin-left: 5px;
            }

            .questionMark > svg {
                display: block;
                height: 14px;
                fill: currentColor;
            }

            .safetyDepthLabel {
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                margin-top: 15px;
                margin-bottom: 10px;
            }
            `
        ];
    }

    render()
    {
        const tooltipPositions = ["right", "top", "left", "bottom"];

        return html`
        <voyage-popup ?shown=${this.shown} header-text="Map Depth Layers" accept-button-text="Save"
                      @accept=${() => this._accept()}
                      @close=${() => this._cancel() }>
            <div class="container">
                <div class="warning">
                    Take in consideration the characteristics of your vessel when changing these values.
                </div>
                <div class="depthGraph">
                    <div class="leftLabel">SHORE</div>
                    <div></div>
                    <div class="depthArea shore"></div>
                    <div></div>
                    <div></div>
                    
                    <div class="leftLabel">INTERTIDAL AREA</div>
                    <div class="nonNavigable">NON-NAVIGABLE</div>
                    <div class="depthArea intertidal"></div>
                    <div></div>
                    <div></div>

                    <div class="leftLabel">VERY SHALLOW</div>
                    <div class="depthArea veryShallow"></div>
                    <div></div>
                    <div></div>

                    <div class="leftLabel">MEDIUM SHALLOW</div>
                    <div class="navigable">NAVIGABLE</div>
                    <div class="depthArea mediumShallow">
                        <div class="depthInput insideInput">
                            <input type="number" data-test="mediumShallow" min="0" step="1" .value=${this._shallowContour}
                                   @input=${(evt: InputEvent) => this._shallowContour = Number((<HTMLInputElement>evt.target!).value)}> m
                        </div>
                    </div>
                    <div></div>
                    <div class="rightLabelContainer">
                        <div class="rightLabel">
                            Shallow Contour
                            <div class="questionMark">
                                ${SvgIcons.lightQuestionCircle}
                                <voyage-tooltip-proxy text="When depth is LESS than the<br>shallow contour, it will be<br>symbolised in dark blue,<br>otherwise in light blue."
                                                      html-content
                                                      .positionPriority=${tooltipPositions}></voyage-tooltip-proxy>
                            </div>
                        </div>
                    </div>

                    <div class="leftLabel">MEDIUM WATER</div>
                    <div class="depthArea mediumWater">
                        <div class="depthInput insideInput">
                            <input type="number" data-test="mediumWater" min="0" step="1" .value=${this._safetyContour}
                                   @input=${(evt: InputEvent) => this._safetyContour = Number((<HTMLInputElement>evt.target!).value)}> m
                        </div>
                    </div>
                    <div></div>
                    <div class="rightLabelContainer">
                        <div class="rightLabel">
                            Safety Contour
                            <div class="questionMark">
                                ${SvgIcons.lightQuestionCircle}
                                <voyage-tooltip-proxy text="When depth is LESS than the<br>safety contour, it will be<br>symbolised in light blue,<br>otherwise in light grey."
                                                      html-content
                                                      .positionPriority=${tooltipPositions}></voyage-tooltip-proxy>
                            </div>
                        </div>
                    </div>

                    <div class="leftLabel">DEEP WATER</div>
                    <div class="depthArea deepWater">
                        <div class="depthInput insideInput">
                            <input type="number" data-test="deepWater" min="0" step="1" .value=${this._deepContour}
                                   @input=${(evt: InputEvent) => this._deepContour = Number((<HTMLInputElement>evt.target!).value)}> m
                        </div>
                    </div>
                    <div></div>
                    <div class="rightLabelContainer">
                        <div class="rightLabel">
                            Deep Contour
                            <div class="questionMark">
                                ${SvgIcons.lightQuestionCircle}
                                <voyage-tooltip-proxy text="When depth is LESS than the<br>deep contour, it will be<br>symbolised in grey,<br>otherwise in light grey."
                                                      html-content
                                                      .positionPriority=${tooltipPositions}></voyage-tooltip-proxy>
                            </div>
                        </div>
                    </div>

                    <div></div>
                    <div></div>
                    <div class="safetyDepthLabel">Safety Depth</div>
                    <div></div>
                    <div></div>

                    <div></div>
                    <div></div>
                    <div class="depthInput">
                        <input type="number" data-test="safetyDepthLabel" min="0" step="1" .value=${this._safetyDepth}
                               @input=${(evt: InputEvent) => this._safetyDepth = Number((<HTMLInputElement>evt.target!).value)}> m
                    </div>
                    <div></div>
                    <div></div>

                </div>
            </div>
        </voyage-popup>
        `;
    }

    @property({attribute: false}) private _safetyContour: number = 0;
    @property({attribute: false}) private _shallowContour: number = 0;
    @property({attribute: false}) private _deepContour: number = 0;
    @property({attribute: false}) private _safetyDepth: number = 0;

    private _accept()
    {
        MgLayers!.setChartDepths({
            safetyContour: this._safetyContour,
            shallowContour: this._shallowContour,
            deepContour: this._deepContour,
            safetyDepth: this._safetyDepth
        })
        this.dispatchEvent(new Event("close"));
    }

    private _cancel()
    {
        this._readDepths();
        this.dispatchEvent(new Event("close"));
    }

    private _readDepths()
    {
        const depths = MgLayers!.chartDepths;
        this._safetyContour = depths.safetyContour;
        this._shallowContour = depths.shallowContour;
        this._deepContour = depths.deepContour;
        this._safetyDepth = depths.safetyDepth;
    }
};
